import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { Location } from 'js/lib/useRouter';

import {
  dropMessages,
  setContinuousChatSessionId,
  setContinuousChatSessionStorageKey,
} from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import {
  getContinuousChatSessionStorageKey,
  persistentContinuousChatSessionId,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/sessionUtils';

type UseFetchChatHistoryOnVisibilityChangeProps = {
  /** Whether continuous chat is enabled */
  enableContinuousChat: boolean;
  /** Function to fetch chat history */
  fetchChatHistory?: (sessionId: string) => void;
  /** Current location object */
  location: Location;
  /** Course ID */
  courseId?: string;
  /** Custom chat session key */
  chatSessionKey?: string;
};

/**
 * Hook that handles fetching chat history when user focuses on a browser tab or window
 * when continuous chat is enabled. This ensures chat history is up-to-date
 * if a user has a chat open in multiple tabs or browser windows.
 *
 * Uses both Page Visibility API (for tab switching) and window focus/blur events
 * (for switching between different browser windows) to provide comprehensive coverage.
 */
export const useFetchChatHistoryOnVisibilityChange = ({
  enableContinuousChat,
  fetchChatHistory,
  location,
  courseId,
  chatSessionKey,
}: UseFetchChatHistoryOnVisibilityChangeProps) => {
  const { messages } = useSelector((state: GlobalState) => ({
    messages: state.messages.messages,
  }));
  const [isWindowFocusListenerActive, setIsWindowFocusListenerActive] = useState(true);

  useEffect(() => {
    const fetchChatHistoryIfNeeded = () => {
      if (enableContinuousChat && fetchChatHistory) {
        const sessionStorageKey = getContinuousChatSessionStorageKey(location, courseId, chatSessionKey);
        const sanitizedChatSessionId = persistentContinuousChatSessionId.get(sessionStorageKey);
        setContinuousChatSessionStorageKey(sessionStorageKey);
        setContinuousChatSessionId(sanitizedChatSessionId);

        // if there are messages in the store but sessionId is now empty, we have stale messages likely due
        // to new session started in another tab
        const hasStaleSession = messages.length > 0 && !sanitizedChatSessionId;

        // clear stale chat
        if (hasStaleSession) {
          dropMessages();
        } else if (sanitizedChatSessionId) {
          // this sessionId might be the same or a different one since this tab/window is loaded,
          // in either case this will fetch the chat history by latest sessionId
          fetchChatHistory(sanitizedChatSessionId);
        }
      }
    };

    const handleVisibilityChange = () => {
      // Page Visibility API - handles tab switching within the same browser window
      if (!document.hidden) {
        fetchChatHistoryIfNeeded();
      }
    };

    const handleWindowFocus = () => {
      // Window focus event - handles switching between different browser windows
      // Also check document.hasFocus() to ensure we actually have focus
      if (document.hasFocus() && isWindowFocusListenerActive) {
        fetchChatHistoryIfNeeded();
      }
    };

    // Listen for both visibility changes (tab switching) and window focus (window switching)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [
    fetchChatHistory,
    enableContinuousChat,
    location,
    courseId,
    chatSessionKey,
    messages.length,
    isWindowFocusListenerActive,
  ]);

  return {
    setIsWindowFocusListenerActive,
  };
};
